"use client";

import React, { useEffect, useRef, useState } from 'react';
import { Zap, Flame, Settings2, CircleCheck, Wrench, Cpu } from 'lucide-react';
import { Card, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.1,
  rootMargin = '0px'
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) observer.unobserve(entry.target);
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const brands = [{
  id: 'miller',
  name: 'Miller',
  icon: Zap,
  description: 'Comprehensive repair, calibration, and maintenance for Miller welding systems. We minimize downtime with expert diagnostics.',
  containerClass: 'bg-blue-950/10 border-blue-900/30',
  headerTextClass: 'text-blue-400',
  badgeClass: 'border-blue-800/50 text-blue-300 bg-blue-950/30 hover:bg-blue-900/50'
}, {
  id: 'lincoln',
  name: 'Lincoln Electric',
  icon: Settings2,
  description: 'Expert diagnostics and heavy-duty service for Lincoln welders and engine-driven equipment.',
  containerClass: 'bg-red-950/10 border-red-900/30',
  headerTextClass: 'text-red-400',
  badgeClass: 'border-red-800/50 text-red-300 bg-red-950/30 hover:bg-red-900/50'
}, {
  id: 'hypertherm',
  name: 'Hypertherm',
  icon: Flame,
  description: 'Precision repair and component-level troubleshooting for Hypertherm plasma cutting systems.',
  containerClass: 'bg-orange-950/10 border-orange-900/30',
  headerTextClass: 'text-orange-400',
  badgeClass: 'border-orange-800/50 text-orange-300 bg-orange-950/30 hover:bg-orange-900/50'
}];
const models = [{
  brandId: 'miller',
  name: 'Millermatic Series'
}, {
  brandId: 'miller',
  name: 'Dynasty TIG Systems'
}, {
  brandId: 'miller',
  name: 'Bobcat Engine Drives'
}, {
  brandId: 'miller',
  name: 'Syncrowave Series'
}, {
  brandId: 'miller',
  name: 'XMT Multiprocess'
}, {
  brandId: 'miller',
  name: 'Trailblazer Series'
}, {
  brandId: 'lincoln',
  name: 'Vantage Engine Drives'
}, {
  brandId: 'lincoln',
  name: 'Ranger Series'
}, {
  brandId: 'lincoln',
  name: 'Power MIG Systems'
}, {
  brandId: 'lincoln',
  name: 'Square Wave TIG'
}, {
  brandId: 'lincoln',
  name: 'Idealarc Series'
}, {
  brandId: 'lincoln',
  name: 'Flextec Multiprocess'
}, {
  brandId: 'hypertherm',
  name: 'Powermax 30/45/65'
}, {
  brandId: 'hypertherm',
  name: 'Powermax 85/105/125'
}, {
  brandId: 'hypertherm',
  name: 'Maxpro Series'
}, {
  brandId: 'hypertherm',
  name: 'Edge Connect CNC'
}, {
  brandId: 'hypertherm',
  name: 'HyPerformance Plasma'
}, {
  brandId: 'hypertherm',
  name: 'MicroEDGE Pro'
}];
const faults = [{
  brandId: 'miller',
  name: 'Inverter Board Failure'
}, {
  brandId: 'miller',
  name: 'Wire Feed Motor Issues'
}, {
  brandId: 'miller',
  name: 'Display & Control Malfunctions'
}, {
  brandId: 'miller',
  name: 'Contactor Replacement'
}, {
  brandId: 'lincoln',
  name: 'Chopper Board Repair'
}, {
  brandId: 'lincoln',
  name: 'Rotor/Stator Rewinds'
}, {
  brandId: 'lincoln',
  name: 'Output Diode Replacement'
}, {
  brandId: 'lincoln',
  name: 'PC Board Diagnostics'
}, {
  brandId: 'hypertherm',
  name: 'Torch Height Control (THC)'
}, {
  brandId: 'hypertherm',
  name: 'Gas Valve Solenoids'
}, {
  brandId: 'hypertherm',
  name: 'Power Supply Faults'
}, {
  brandId: 'hypertherm',
  name: 'DSP Board Repair'
}];
function BrandSection({
  brand,
  index
}: {
  brand: typeof brands[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const Icon = brand.icon;
  const brandModels = models.filter(m => m.brandId === brand.id);
  const brandFaults = faults.filter(f => f.brandId === brand.id);
  return <div ref={ref} data-index={index} className={`relative overflow-hidden rounded-2xl border p-8 md:p-12 transition-all duration-1000 ease-out ${brand.containerClass} ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
            {}
            <div className="absolute -top-24 -right-24 size-96 bg-background/5 blur-3xl rounded-full pointer-events-none" />
            
            <div className="relative z-10 grid gap-12 lg:grid-cols-[1fr_2fr]">
                {}
                <div className="flex flex-col gap-8">
                    <div>
                        <div className="flex items-center gap-4 mb-4">
                            <div className={`p-3 rounded-xl bg-background/50 border border-border/50 backdrop-blur-sm ${brand.headerTextClass}`}>
                                <Icon className="size-8" />
                            </div>
                            <h3 className="text-3xl font-bold tracking-tight text-foreground">
                                {brand.name}
                            </h3>
                        </div>
                        <p className="text-muted-foreground text-lg leading-relaxed">
                            {brand.description}
                        </p>
                    </div>

                    <div className="space-y-4">
                        <h4 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                            <Wrench className="size-4" />
                            Common Faults Addressed
                        </h4>
                        <div className="flex flex-wrap gap-2">
                            {brandFaults.map((fault, fIndex) => <Badge key={fault.name} data-index={fIndex} variant="outline" className={`px-3 py-1.5 text-sm font-medium transition-colors ${brand.badgeClass}`}>
                                    <Cpu className="size-3.5 mr-1.5 opacity-70" />
                                    {fault.name}
                                </Badge>)}
                        </div>
                    </div>
                </div>

                {}
                <div className="space-y-6">
                    <h4 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                        <Settings2 className="size-4" />
                        Supported Equipment Series
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {brandModels.map((model, mIndex) => <Card key={model.name} data-index={mIndex} className="bg-background/40 border-border/40 backdrop-blur-md hover:bg-background/60 transition-colors">
                                <CardHeader className="p-4">
                                    <CardTitle className="text-base font-medium flex items-center gap-3 text-foreground/90">
                                        <CircleCheck className={`size-4 ${brand.headerTextClass}`} />
                                        {model.name}
                                    </CardTitle>
                                </CardHeader>
                            </Card>)}
                    </div>
                </div>
            </div>
        </div>;
}
export default function EquipmentFocus() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section id="equipment-focus" className="bg-background py-24 relative overflow-hidden">
            {}
            <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px] pointer-events-none" />
            
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                <div ref={headerRef} className={`max-w-3xl mb-16 transition-all duration-700 ease-out ${isHeaderVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                    <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-foreground mb-6">
                        Specialized <span className="text-primary">Equipment Focus</span>
                    </h2>
                    <p className="text-xl text-muted-foreground leading-relaxed">
                        Our technicians possess deep, brand-specific expertise. We maintain an extensive inventory of OEM parts and utilize state-of-the-art diagnostic tools tailored for the industry's leading manufacturers.
                    </p>
                </div>

                <div className="flex flex-col gap-8">
                    {brands.map((brand, index) => <BrandSection key={brand.id} brand={brand} index={index} />)}
                </div>
            </div>
        </section>;
}